import SearchForm from './search-form';

interface PredictiveSearchElement extends Element {
  cachedResults: Record<string, string>;
}

class PredictiveSearch extends SearchForm {
  private cachedResults: Record<string, string> = {};
  private predictiveSearchResults: HTMLElement;
  private allPredictiveSearchInstances: NodeListOf<Element>;
  private isOpen: boolean = false;
  private abortController: AbortController = new AbortController();
  private searchTerm: string = '';
  private statusElement?: HTMLElement;
  private loadingText?: string;
  private resultsMaxHeight?: number | false;
  private initialResultsMarkup: string = '';

  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]') as HTMLElement;
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    // Store the initial/default markup so we can restore it when the query is cleared
    this.initialResultsMarkup = this.predictiveSearchResults?.innerHTML || '';
    this.setupEventListeners();
  }

  setupEventListeners(): void {
    if (this.input?.form) {
      this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    }

    this.input?.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    // this.addEventListener('keyup', this.onKeyup.bind(this));
    // this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery(): string {
    return this.input?.value.trim() || '';
  }

  onChange(event: Event): void {
    super.onChange(event);
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      // When cleared, abort any pending requests and restore the default content
      this.abortController.abort();
      this.abortController = new AbortController();
      this.renderDefaultResults();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event: Event): void {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  onFormReset(event: Event): void {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.renderDefaultResults();
    }
  }

  onFocus(): void {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange(new Event('change'));
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut(): void {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  // onKeyup(event: KeyboardEvent): void {
  //   if (!this.getQuery().length) this.close(true);
  //   event.preventDefault();

  //   switch (event.code) {
  //     case 'ArrowUp':
  //       this.switchOption('up');
  //       break;
  //     case 'ArrowDown':
  //       this.switchOption('down');
  //       break;
  //     case 'Enter':
  //       this.selectOption();
  //       break;
  //   }
  // }

  // onKeydown(event: KeyboardEvent): void {
  //   if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
  //     event.preventDefault();
  //   }
  // }

  updateSearchForTerm(previousTerm: string, newTerm: string): void {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]') as HTMLElement;
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      const matches = currentButtonText.match(new RegExp(previousTerm, 'g'));
      if (matches && matches.length > 1) {
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  // switchOption(direction: 'up' | 'down'): void {
  //   if (!this.getAttribute('open')) return;

  //   const moveUp = direction === 'up';
  //   const selectedElement = this.querySelector('[aria-selected="true"]') as HTMLElement;

  //   const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
  //     (element) => (element as HTMLElement).offsetParent !== null,
  //   ) as HTMLElement[];
  //   let activeElementIndex = 0;

  //   if (moveUp && !selectedElement) return;

  //   let selectedElementIndex = -1;
  //   let i = 0;

  //   while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
  //     if (allVisibleElements[i] === selectedElement) {
  //       selectedElementIndex = i;
  //     }
  //     i++;
  //   }

  //   if (this.statusElement) {
  //     this.statusElement.textContent = '';
  //   }

  //   if (!moveUp && selectedElement) {
  //     activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
  //   } else if (moveUp) {
  //     activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
  //   }

  //   if (activeElementIndex === selectedElementIndex) return;

  //   const activeElement = allVisibleElements[activeElementIndex];

  //   activeElement.setAttribute('aria-selected', 'true');
  //   if (selectedElement) selectedElement.setAttribute('aria-selected', 'false');

  //   this.input?.setAttribute('aria-activedescendant', activeElement.id);
  // }

  // selectOption(): void {
  //   const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]') as HTMLElement;

  //   if (selectedOption) selectedOption.click();
  // }

  getSearchResults(searchTerm: string): void {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const url = `${window.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`;
    fetch(url, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status.toString());
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search')?.innerHTML;

        if (resultsMarkup) {
          this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
            (predictiveSearchInstance as PredictiveSearchElement).cachedResults[queryKey] = resultsMarkup;
          });
          this.renderSearchResults(resultsMarkup);
        }
      })
      .catch((error) => {
        if (error?.code === 20) {
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState(): void {
    this.statusElement = this.statusElement || (this.querySelector('.predictive-search-status') as HTMLElement);
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text') || '';

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', 'true');
  }

  setLiveRegionText(statusText: string): void {
    if (this.statusElement) {
      this.statusElement.setAttribute('aria-hidden', 'false');
      this.statusElement.textContent = statusText;

      setTimeout(() => {
        this.statusElement?.setAttribute('aria-hidden', 'true');
      }, 1000);
    }
  }

  renderSearchResults(resultsMarkup: string): void {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', 'true');

    this.setLiveRegionResults();
    this.open();
  }

  private renderDefaultResults(): void {
    if (this.predictiveSearchResults && this.initialResultsMarkup) {
      this.predictiveSearchResults.innerHTML = this.initialResultsMarkup;
    }
    this.removeAttribute('results');
    this.open();
  }

  setLiveRegionResults(): void {
    this.removeAttribute('loading');
    const countElement = this.querySelector('[data-predictive-search-live-region-count-value]') as HTMLElement;
    if (countElement) {
      this.setLiveRegionText(countElement.textContent || '');
    }
  }

  getResultsMaxHeight(): number {
    this.resultsMaxHeight =
      window.innerHeight - (document.querySelector('.section-header')?.getBoundingClientRect().bottom || 0);
    return this.resultsMaxHeight;
  }

  open(): void {
    // this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight
    //   ? `${this.resultsMaxHeight}px`
    //   : `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', 'true');
    this.input?.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
  }

  close(clearSearchTerm: boolean = false): void {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm: boolean = false): void {
    if (clearSearchTerm) {
      if (this.input) {
        this.input.value = '';
      }
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]') as HTMLElement;

    if (selected) selected.setAttribute('aria-selected', 'false');

    this.input?.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input?.setAttribute('aria-expanded', 'false');
    this.resultsMaxHeight = false;
    // this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);

export default PredictiveSearch;
